import { find, first, pathOr, pick, pipe } from 'remeda';
import invariant from 'tiny-invariant';

import {
    Element,
    ELEMENT_TYPES,
    type SummaryListElement,
    type SummaryListSummaryElement,
} from '@@editor/helpers/Element';
import {
    isUnitySummaryList,
    isUnityUnorderedList,
    type UnitySummaryList,
    type UnityUnorderedList,
} from '@@editor/typings/UnityElements';
import { castArray } from '@@scripts/utils/arrayUtils';

import { CURRENT_VERSION } from '../constants';
import { createElementWithChildren } from '../deserializeNodes';
import { type ElementRule, type Next } from '../types';

const isValidUnitySummaryListSummary = (value: any): value is UnitySummaryList['summary'] =>
    isUnityUnorderedList(value) && value.items.length > 0;

const addEmptySummaryList = (value) => {
    if (isValidUnitySummaryListSummary(value)) {
        return value;
    }

    return {
        type: ELEMENT_TYPES.UNORDERED_LIST,
        version: CURRENT_VERSION,
        items: [
            {
                type: ELEMENT_TYPES.TEXT,
                version: CURRENT_VERSION,
                items: [
                    {
                        text: '',
                        version: CURRENT_VERSION,
                        type: ELEMENT_TYPES.TEXTITEM,
                    },
                ],
            },
        ],
    } satisfies UnitySummaryList['summary'];
};

const deserializeSummary =
    (next: Next<SummaryListSummaryElement['children']>) =>
    (summaryListSummary: UnitySummaryList['summary']) =>
        pipe(
            summaryListSummary,
            addEmptySummaryList,
            next,
            castArray,
            createElementWithChildren(ELEMENT_TYPES.SUMMARY_LIST_SUMMARY),
        );

const serializeSummary =
    (next: Next<UnityUnorderedList>) => (children: SummaryListElement['children']) =>
        pipe(
            children,
            find(Element.isSummaryListSummaryElement),
            pathOr(['children'] as const, []),
            first,
            next,
        );

type Rule = ElementRule<SummaryListElement, UnitySummaryList>;

const summaryListRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnitySummaryList(element)) {
            invariant(next, 'Cannot deserialize SummaryList without a `next` function');

            const { summary, type } = element;

            return {
                type,
                data: pick(element, ['templateElement']),
                children: [deserializeSummary(next)(summary)],
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isSummaryListElement(node)) {
            invariant(next, 'Cannot serialize SummaryList without a `next` function');

            const { data, type, children } = node;

            return {
                ...pick(data, ['templateElement']),
                version: CURRENT_VERSION,
                type,
                summary: serializeSummary(next)(children),
            };
        }
    },
};

export default summaryListRules;
